import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Minus, Plus, ShieldCheck, Lock, Loader2, ArrowRight, Infinity as InfinityIcon, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { HUME } from "@/lib/biohackerBrand";

/**
 * Team / Performance checkout configurator.
 *
 * Supports 3 SKUs:
 *  - team_foundational ($299/yr, 20 base seats + $12.99/seat extra) ← legacy
 *  - team              ($1,495/yr, 40 seats, no overage)            ← new
 *  - performance       ($2,995/yr, unlimited)                       ← new
 *
 * Only the legacy SKU shows the seat configurator. The new flat-rate
 * tiers just confirm the total and route straight to Stripe Checkout.
 */
const PKG_META = {
  team_foundational: { label: "Foundational Team", base: 299.00, seats: 20, extra_seat: 12.99, max_extra: 80, allow_extra: true,  cycle: "/year" },
  team:              { label: "Team — Annual",     base: 1495.00, seats: 40, extra_seat: 25.00, max_extra: 200, allow_extra: true,  cycle: "/year" },
  performance:       { label: "Performance — Annual", base: 2995.00, seats: null, extra_seat: 0, max_extra: 0, allow_extra: false, cycle: "/year" },
};

export default function TeamCheckoutDialog({ open, onOpenChange, packageId = "team", initialExtra = 0, foundationalRemaining }) {
  const pkg = PKG_META[packageId] || PKG_META.team;
  const [extra, setExtra] = useState(initialExtra || 0);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  // Hume Band bundle — added to the same Stripe checkout so a coach can
  // seat their team and equip every athlete with a physiological wearable
  // in one transaction. Server re-computes pricing from the tier ladder.
  const [bandsQty, setBandsQty] = useState(0);
  const [bandsWarranty, setBandsWarranty] = useState(false);

  // Discount code (applies to plan + seats, not Hume hardware)
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null); // {code, percent_off, label}
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (open) {
      setExtra(initialExtra || 0);
      setAgreed(false);
      setBusy(false);
      setBandsQty(0);
      setBandsWarranty(false);
      setPromo("");
      setApplied(null);
    }
  }, [open, initialExtra]);

  const applyPromo = async () => {
    if (!promo.trim()) return;
    setChecking(true);
    try {
      const { data } = await api.get(`/billing/discount/validate?code=${encodeURIComponent(promo.trim())}`);
      if (data.valid) {
        setApplied(data);
        toast.success(`${data.percent_off}% discount applied — ${data.label || data.code}`);
      } else {
        setApplied(null);
        toast.error("Invalid or expired discount code");
      }
    } catch { toast.error("Could not validate code"); } finally { setChecking(false); }
  };

  const seatAmount = useMemo(() => +(extra * pkg.extra_seat).toFixed(2), [extra, pkg.extra_seat]);

  // Mirror backend HUME_TIERS ladder for a live preview — server clamps + verifies.
  const bandTier = useMemo(() => {
    if (!bandsQty) return null;
    return HUME.tiers.find((t) => bandsQty >= t.min_qty && (t.max_qty == null || bandsQty <= t.max_qty));
  }, [bandsQty]);
  const bandUnit = bandTier?.unit_price_usd || HUME.price_usd;
  const bandsSubtotal = +(bandUnit * bandsQty).toFixed(2);
  const bandsWarrantyCost = bandsWarranty ? +(50 * bandsQty).toFixed(2) : 0;
  const bandsTotal = +(bandsSubtotal + bandsWarrantyCost).toFixed(2);

  const discountAmt = useMemo(
    () => (applied ? +(((pkg.base + seatAmount) * applied.percent_off) / 100).toFixed(2) : 0),
    [applied, pkg.base, seatAmount],
  );
  const total = useMemo(
    () => +Math.max(0, pkg.base + seatAmount - discountAmt + bandsTotal).toFixed(2),
    [seatAmount, pkg.base, bandsTotal, discountAmt],
  );
  const totalSeats = pkg.seats == null ? null : pkg.seats + extra;
  const setExtraSafe = (v) => setExtra(Math.max(0, Math.min(pkg.max_extra, Math.floor(Number(v) || 0))));
  const setBandsSafe = (v) => setBandsQty(Math.max(0, Math.min(500, Math.floor(Number(v) || 0))));

  const checkout = async () => {
    if (!agreed) { toast.error("Please accept the recurring billing terms to continue"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout/team", {
        origin_url: window.location.origin,
        additional_seats: pkg.allow_extra ? extra : 0,
        package_id: packageId,
        hume_bands_qty: bandsQty,
        hume_warranty_10yr: bandsWarranty,
        discount_code: applied?.code || null,
      });
      if (data.free) {
        toast.success("100% discount applied — your plan is now active! 🎉");
        onOpenChange(false);
        setTimeout(() => window.location.reload(), 1200);
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white sm:max-w-lg" data-testid="team-checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tighter">
            Confirm your {pkg.label}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {packageId === "team_foundational"
              ? "$299/yr includes up to 20 player seats. Buy extra seats at $12.99/seat to cover your full roster."
              : packageId === "team"
                ? "$1,495/yr · up to 40 athletes, 5 coaches, 1 team workspace. Full ArmIQ + SpeedIQ + MotionIQ + RecoveryIQ access."
                : "$2,995/yr · unlimited athletes, unlimited coaches, multiple teams, departmental roles, API access."}
          </DialogDescription>
        </DialogHeader>

        {packageId === "team_foundational" && foundationalRemaining > 0 && (
          <div className="rounded-lg p-3 text-xs flex items-center gap-2" style={{ backgroundColor: "rgba(217,4,41,0.10)", border: "1px solid rgba(217,4,41,0.35)" }}>
            <ShieldCheck className="h-4 w-4" style={{ color: "var(--brand-secondary)" }} />
            <span className="text-white/85">Foundational pricing — <span className="font-mono-num font-bold text-white">{foundationalRemaining}</span> of 500 lifetime slots remaining at $299/yr.</span>
          </div>
        )}

        {pkg.allow_extra && (
          <div className="rounded-xl p-4 mt-2" style={{ backgroundColor: "var(--ink-1)" }}>
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Additional player seats</div>
            <div className="mt-3 flex items-center gap-3">
              <Button type="button" variant="outline" size="icon" onClick={() => setExtraSafe(extra - 1)} disabled={extra <= 0} className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-11 w-11" data-testid="seats-minus">
                <Minus className="h-4 w-4" />
              </Button>
              <Input type="number" min={0} max={pkg.max_extra} value={extra} onChange={(e) => setExtraSafe(e.target.value)} className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white text-center font-display font-black text-2xl h-11 w-24" data-testid="seats-input" />
              <Button type="button" variant="outline" size="icon" onClick={() => setExtraSafe(extra + 1)} disabled={extra >= pkg.max_extra} className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-11 w-11" data-testid="seats-plus">
                <Plus className="h-4 w-4" />
              </Button>
              <div className="flex-1 text-xs text-white/55 leading-snug">
                Total seats: <span className="font-mono-num font-bold text-white" data-testid="total-seats">{totalSeats}</span>
                <br /><span className="text-white/40">${pkg.extra_seat.toFixed(2)} per seat per year · max +{pkg.max_extra}</span>
              </div>
            </div>
          </div>
        )}

        {/* Hume Band bundle */}
        <div className="rounded-xl p-4 mt-2" style={{ backgroundColor: "var(--ink-1)" }} data-testid="checkout-hume-bundle">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                   style={{ backgroundColor: "rgba(217,4,41,0.12)", color: "#F43F5E" }}>
                <HeartPulse className="h-4 w-4" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Equip your athletes</div>
                <div className="font-display font-black text-white text-sm">Hume Band bundle · optional</div>
                <div className="text-[11px] text-white/50 mt-0.5">
                  {bandTier?.label || `Starts at $${HUME.price_usd}/band · bulk tiers auto-applied`}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-display font-black text-white text-lg tabular-nums" data-testid="checkout-bands-subtotal">
                ${bandsSubtotal.toFixed(2)}
              </div>
              {bandTier && bandTier.unit_price_usd < HUME.tiers[0].unit_price_usd && (
                <div className="text-[10px] text-emerald-300">
                  ${(HUME.tiers[0].unit_price_usd - bandTier.unit_price_usd).toFixed(0)}/band saved
                </div>
              )}
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <Button type="button" variant="outline" size="icon" onClick={() => setBandsSafe(bandsQty - 1)} disabled={bandsQty <= 0} className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-11 w-11" data-testid="bands-minus">
              <Minus className="h-4 w-4" />
            </Button>
            <Input type="number" min={0} max={500} value={bandsQty} onChange={(e) => setBandsSafe(e.target.value)}
              className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white text-center font-display font-black text-2xl h-11 w-24"
              data-testid="bands-input" />
            <Button type="button" variant="outline" size="icon" onClick={() => setBandsSafe(bandsQty + 1)} disabled={bandsQty >= 500} className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-11 w-11" data-testid="bands-plus">
              <Plus className="h-4 w-4" />
            </Button>
            {totalSeats != null && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setBandsSafe(totalSeats)}
                className="text-xs text-fuchsia-300 hover:text-white"
                data-testid="bands-match-roster"
              >
                Match roster ({totalSeats})
              </Button>
            )}
          </div>
          {bandsQty > 0 && (
            <label className="mt-3 flex items-start gap-2 rounded-md border border-white/12 bg-white/[0.02] p-2.5 cursor-pointer text-xs text-white/75 hover:bg-white/[0.05]"
                   data-testid="bands-warranty-toggle">
              <Checkbox
                checked={bandsWarranty}
                onCheckedChange={(v) => setBandsWarranty(!!v)}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--brand-secondary)] data-[state=checked]:border-[var(--brand-secondary)]"
                data-testid="bands-warranty-checkbox"
              />
              <span>
                Add <b className="text-white">10-year warranty</b> on every band (+${(50 * bandsQty).toFixed(2)}) — full replacement, sensor coverage.
              </span>
            </label>
          )}
        </div>

        <div className="rounded-xl p-4 space-y-2" style={{ backgroundColor: "var(--ink-1)" }}>
          {/* Discount code */}
          <div className="flex items-center gap-2 pb-2 border-b border-[var(--ink-border)]">
            <Input placeholder="Discount code" value={promo}
              onChange={(e) => { setPromo(e.target.value.toUpperCase()); if (applied) setApplied(null); }}
              className="bg-[var(--ink-2)] border-[var(--ink-border)] text-white h-9 text-sm uppercase"
              data-testid="discount-code-input" />
            <Button type="button" variant="outline" size="sm" onClick={applyPromo} disabled={checking || !promo.trim()}
              className="border-white/15 text-white bg-white/5 hover:bg-white/10 h-9" data-testid="discount-apply-btn">
              {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : applied ? "Applied ✓" : "Apply"}
            </Button>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/70">
              {pkg.label}{" "}
              {pkg.seats == null ? (
                <span className="inline-flex items-center gap-1 text-white/50"><InfinityIcon className="h-3 w-3" /> unlimited seats</span>
              ) : (
                <span className="text-white/50">({pkg.seats} seats)</span>
              )}
            </span>
            <span className="font-mono-num text-white">${pkg.base.toFixed(2)}</span>
          </div>
          {pkg.allow_extra && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{extra} additional seat{extra === 1 ? "" : "s"} × ${pkg.extra_seat.toFixed(2)}</span>
              <span className="font-mono-num text-white" data-testid="seat-amount">${seatAmount.toFixed(2)}</span>
            </div>
          )}
          {bandsQty > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">
                {bandsQty} Hume Band{bandsQty === 1 ? "" : "s"} × ${bandUnit}
                <span className="ml-1 text-[10px] uppercase tracking-wider text-fuchsia-300/70">{bandTier?.label?.replace(/·.*$/, "").trim()}</span>
              </span>
              <span className="font-mono-num text-white" data-testid="bands-line-amount">${bandsSubtotal.toFixed(2)}</span>
            </div>
          )}
          {bandsQty > 0 && bandsWarranty && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">10-year warranty × {bandsQty}</span>
              <span className="font-mono-num text-white" data-testid="bands-warranty-amount">${bandsWarrantyCost.toFixed(2)}</span>
            </div>
          )}
          {applied && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-emerald-300">Discount · {applied.code} ({applied.percent_off}% off plan + seats)</span>
              <span className="font-mono-num text-emerald-300" data-testid="discount-amount">−${discountAmt.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-[var(--ink-border)] pt-2 mt-2 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.3em] font-bold uppercase text-white/60">Total due today</span>
            <span className="font-display font-black text-3xl font-mono-num text-white" data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>
        </div>

        <label className="flex items-start gap-3 mt-2 cursor-pointer text-xs text-white/65 leading-relaxed" data-testid="checkout-agree-label">
          <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--brand-secondary)] data-[state=checked]:border-[var(--brand-secondary)]" data-testid="checkout-agree" />
          <span>
            This is a recurring annual subscription. By continuing I agree to the <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline hover:text-white">cancellation policy</a> and authorize ArmIQ to charge my payment method at the prices, frequency, and dates listed on this page until my order is fulfilled or I cancel, if permitted.
          </span>
        </label>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-white/15 text-white bg-white/5 hover:bg-white/10">Back</Button>
          <Button onClick={checkout} disabled={busy || !agreed} className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="team-checkout-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> {total === 0 ? "Activate for $0.00" : `Pay $${total.toFixed(2)}`} <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
